import { forwardRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, ChevronUp, ShoppingCart, Trash2 } from "lucide-react";
import client, { apiErrorMessage } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { formatRupees } from "./RoomsSection";

const DEFAULT_TERMS = "Advance 50%, balance on delivery. Installation included.";

const itemTitle = (it) =>
  it.window ? `${it.window.windowType || "Window"} ${it.window.width}×${it.window.height}` : "Window item";

const itemSpecs = (it) => [it.design?.fabric, it.design?.colour, it.design?.pleat, `Qty ${it.quantity}`].filter(Boolean).join(" · ");

// Collapsed summary card that expands into the project cart and quotation step.
const DesignProposal = forwardRef(function DesignProposal({ projectId, rooms, items, open, setOpen, onCreateQuotation, creating }, ref) {
  const queryClient = useQueryClient();
  const [terms, setTerms] = useState(DEFAULT_TERMS);
  const [pendingRemoveId, setPendingRemoveId] = useState(null);

  const removeCartItem = useMutation({
    mutationFn: (itemId) => client.delete(`/carts/items/${itemId}`),
    onSuccess: () => {
      toast.success("Removed from cart");
      queryClient.invalidateQueries({ queryKey: ["cart", projectId] });
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Failed to remove item")),
  });

  const total = items.reduce((s, it) => s + (it.price || 0), 0);
  const roomsWithItems = rooms.filter((r) => items.some((it) => it.roomId === r._id));

  return (
    <section id="project-cart" ref={ref} className="bg-card border border-border rounded-2xl scroll-mt-20" data-testid="project-cart-summary">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-5 sm:p-6">
        <span className="h-14 w-14 rounded-full bg-accent/10 text-accent flex items-center justify-center shrink-0">
          <ShoppingCart className="h-6 w-6" strokeWidth={1.6} />
        </span>
        <div className="flex-1 min-w-0">
          <h2 className="font-serif text-xl text-foreground">Design Proposal</h2>
          <p className="text-sm text-muted-foreground">
            {items.length
              ? `${items.length} product${items.length === 1 ? "" : "s"} across ${roomsWithItems.length} room${roomsWithItems.length === 1 ? "" : "s"} · ${formatRupees(total)}`
              : "Configure products for each room and create a design proposal."}
          </p>
        </div>
        <Button
          data-testid="open-cart-button"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
          className="self-start sm:self-auto bg-accent hover:bg-accent/90 text-accent-foreground"
        >
          {open ? (
            <>Hide Cart <ChevronUp className="h-4 w-4 ml-1.5" /></>
          ) : (
            <>Open Cart <ArrowRight className="h-4 w-4 ml-1.5" /></>
          )}
        </Button>
      </div>

      {open && (
        <div className="border-t border-border px-5 sm:px-6 py-5 hi-animate-fade-up">
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground">Cart is empty — open a window and configure its product to add items.</p>
          ) : (
            <>
              <div className="space-y-5">
                {roomsWithItems.map((r) => (
                  <div key={r._id}>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{r.name}</p>
                    <ul className="divide-y divide-border rounded-xl border border-border">
                      {items
                        .filter((it) => it.roomId === r._id)
                        .map((it) => (
                          <li key={it._id} className="flex items-center gap-3 p-3" data-testid={`cart-item-${it._id}`}>
                            <Link
                              to={`/windows/${it.windowId}?projectId=${projectId}&roomId=${it.roomId}`}
                              className="flex items-center gap-3 min-w-0 flex-1 group"
                              title="Open this window's design"
                            >
                              <span className="h-14 w-14 rounded-lg bg-secondary shrink-0 overflow-hidden">
                                {it.previewUrl && <img src={it.previewUrl} alt="AI preview" className="h-full w-full object-cover" />}
                              </span>
                              <span className="min-w-0">
                                <span className="block text-sm text-foreground group-hover:text-accent transition-colors truncate">
                                  {itemTitle(it)}
                                  {it.design?.configType && (
                                    <span className="text-muted-foreground"> · {it.design.configType.toLowerCase().replace("_", " ")}</span>
                                  )}
                                </span>
                                <span className="block text-xs text-muted-foreground truncate">{itemSpecs(it)}</span>
                              </span>
                            </Link>
                            <span className="font-medium text-foreground tabular-nums">{formatRupees(it.price)}</span>
                            <button
                              type="button"
                              aria-label="Remove from cart"
                              data-testid={`remove-cart-item-${it._id}`}
                              onClick={() => setPendingRemoveId(it._id)}
                              className="p-1.5 rounded-full text-muted-foreground hover:text-danger hover:bg-danger/10 transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </li>
                        ))}
                    </ul>
                  </div>
                ))}
              </div>

              <div className="mt-5 flex items-baseline justify-end gap-3">
                <span className="text-sm text-muted-foreground">Subtotal</span>
                <span className="font-serif text-2xl text-foreground tabular-nums" data-testid="project-cart-subtotal">
                  {formatRupees(total)}
                </span>
              </div>

              <label htmlFor="quotation-terms" className="block text-sm font-medium text-foreground mt-5 mb-1.5">Quotation terms</label>
              <Textarea id="quotation-terms" data-testid="quotation-terms-input" value={terms} onChange={(e) => setTerms(e.target.value)} />
              <div className="mt-4 flex justify-end">
                <Button
                  data-testid="generate-quotation-button"
                  disabled={creating}
                  className="w-full sm:w-auto bg-accent hover:bg-accent/90 text-accent-foreground"
                  onClick={() => onCreateQuotation(terms)}
                >
                  {creating ? "Creating…" : "Create Quotation"} <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      <AlertDialog open={!!pendingRemoveId} onOpenChange={(o) => !o && setPendingRemoveId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this item from the cart?</AlertDialogTitle>
            <AlertDialogDescription>The window's design and AI previews are kept; only the cart entry goes.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction className="bg-danger hover:bg-danger/90" data-testid="confirm-remove-cart-item" onClick={() => removeCartItem.mutate(pendingRemoveId)}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
});

export default DesignProposal;
